/**
 * Helper object for building the flags for performing one or many various transformations on an image served by Google.
 * Intended to be used in conjunction with the ImageTransformationService.
 *
 * Google's image transformation API is not very well documented at the moment so all of these flags have come from
 * https://stackoverflow.com/questions/25148567/list-of-all-the-app-engine-images-service-get-serving-url-uri-options.
 *
 * Usage:
 * In Typescript
 *    private transformation: ImageTransformation = new ImageTransformation()
 *        .crop()
 *        .flipHorizontally(); // Can chain transformation functions indefinitely.
 *    private sizes: number[] = [128, 256, 512];
 *    private url: string = 'https://someurl.com';
 *
 * In HTML
 *    <img [srcset]="ImageTransformationService.getSrcSetForImage(url, sizes, transformation)" />
 */

export class ImageTransformation {
  private flags: string;

  constructor() {
    this.flags = '';
  }

  /**
   * Adds a flag, specifying that the image should be resized using the provided size. The size will be applied to the
   * largest dimension (height for images in portrait and width for images in landscape).
   * @param size The size in pixels that the longest side should be set to.
   */
  resize(size: number): ImageTransformation {
    this.flags += `s${size}`;
    return this;
  }

  /**
   * Adds a flag, specifying that the image should be cropped to be a square whose size depends on the size of the shortest side.
   */
  crop(): ImageTransformation {
    this.flags += '-c';
    return this;
  }

  /**
   * Same as crop() except the image attempts to crop around faces.
   */
  smartCrop(): ImageTransformation {
    this.flags += '-p';
    return this;
  }

  /**
   * Same as crop() except the image is cropped as a circle instead of a square.
   */
  circularCrop(): ImageTransformation {
    this.flags += '-cc';
    return this;
  }

  /**
   * Adds a flag specifying that the image should be flipped vertically.
   */
  flipVertically(): ImageTransformation {
    this.flags += '-fv';
    return this;
  }

  /**
   * Adds a flag specifying that the image should be flipped horiztonally.
   */
  flipHorizontally(): ImageTransformation {
    this.flags += '-fh';
    return this;
  }

  /**
   * Adds a flag specifying that the image should be rotated the specified number of degrees.
   * @param degrees How many degrees the image should be rotated by. Should be one of 90, 180 or 270.
   */
  rotate(degrees: 90 | 180 | 270): ImageTransformation {
    this.flags += `-r${degrees}`;
    return this;
  }

  /**
   * Adds a flag specifying that the image should be rendered as a JPEG.
   */
  renderAsJpeg(): ImageTransformation {
    this.flags += '-rj';
    return this;
  }

  /**
   * Adds a flag specifying that the image should be rendered as a PNG
   */
  renderAsPng(): ImageTransformation {
    this.flags += '-rp';
    return this;
  }

  /**
   * Adds a flag specifying that the image should be rendered as a WEBP
   */
  renderAsWebP(): ImageTransformation {
    this.flags += '-rw';
    return this;
  }

  /**
   * Adds a flag specifying that the image should be rendered as a GIF
   */
  renderAsGif(): ImageTransformation {
    this.flags += '-rg';
    return this;
  }

  /**
   * Adds a flag specifying that a GIF should be converted into and rendered as an MP4 movie.
   */
  makeMp4(): ImageTransformation {
    this.flags += '-rh';
    return this;
  }

  /**
   * Adds a flag specifying that a GIF should not be animated and show a static image instead.
   */
  killAnimation(): ImageTransformation {
    this.flags += '-k';
    return this;
  }

  /**
   * Adds a flag specifying that a border of the specified width should be added to the image.
   * @param width The width of the border in pixels.
   */
  addBorder(width: number): ImageTransformation {
    this.flags += `-b${width}`;
    return this;
  }

  /**
   * Adds a flag specifying what the colour of the border should be.
   * @param argbHex The hexadecimal representation of the colour, in the format AARRGGBB.
   */
  setBorderColour(argbHex: string): ImageTransformation {
    this.flags += `-c0x${argbHex}`;
    return this;
  }

  /**
   * Adds a flag specifying that the image should include a header to cause the browser to download it.
   */
  makeDownloadable(): ImageTransformation {
    this.flags += '-d';
    return this;
  }

  /**
   * Adds a flag specifying that the quality of the rendered JPEG image should be set to the specified percent.
   * @param qualityPercent A number from 0-100 representing the percent of quality the JPEG should be.
   */
  setJpegQuality(qualityPercent: number): ImageTransformation {
    this.flags += `-l${qualityPercent}`;
    return this;
  }

  /**
     * Adds a flag specifying that the image should be softened by the specified amount.
     * @param softenPct

     */
  soften(softenPct: number): ImageTransformation {
    this.flags += `-fSoften=1,${softenPct},0`;
    return this;
  }

  /**
   * Adds a flag specifying that a vignette should be added to the image with the specified size and colour.
   * @param gradientSize
   * @param argbHex
   */
  vignette(gradientSize: number, argbHex: string): ImageTransformation {
    this.flags += `-fVignette=1,${gradientSize},1.4,0,${argbHex}`;
    return this;
  }

  /**
   * Adds a flag specifying that the colours in the image should be inverted.
   */
  invert(): ImageTransformation {
    this.flags += `-fInvert=1`;
    return this;
  }

  /**
   * Adds a flag specifying that the image should be converted to black and white.
   */
  blackAndWhite(): ImageTransformation {
    this.flags += `-fbw=1`;
    return this;
  }

  /**
   * Returns the current set of flags that have been set as a single string that can be appended to a serving URL.
   */
  getFlags(): string {
    return this.flags;
  }

  /**
   * Gets a value indicating whether this transformation currently has any flags set.
   */
  hasFlags(): boolean {
    return this.flags.length > 0;
  }
}
